import { useMemo } from 'react';
import type {
  ProjectSchema,
  UsageConfigResult,
  UsageCostBreakdown,
} from '../../types';
import { WorkflowSection } from '../layout/UsageConfigSection';
import { evalExpr, isVisible } from '../../utils/expr';

/**
 * Report view: renders a read-only card per calculator section with its
 * cost, cost drivers, and operation drill-down. Adjustable fields live
 * in CalculatorSidebar, so this component never shows sliders/selects.
 */

interface Props {
  schema: ProjectSchema;
  params: Record<string, unknown>;
  breakdown: UsageCostBreakdown | null;
}

function substituteTemplate(
  template: string,
  params: Record<string, unknown>,
): string {
  return template.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_, name) => {
    const v = params[name];
    if (v === undefined || v === null) return '';
    return typeof v === 'number'
      ? Number.isInteger(v)
        ? String(v)
        : v.toFixed(1)
      : String(v);
  });
}

export function CalculatorRenderer({ schema, params, breakdown }: Props) {
  const configByName = useMemo(() => {
    const map: Record<string, UsageConfigResult> = {};
    breakdown?.configs.forEach((c) => {
      map[c.config_name] = c;
    });
    return map;
  }, [breakdown]);

  return (
    <>
      {schema.calculator.sections
        .filter((section) => isVisible(section.visible_when, params))
        .map((section) => {
        const cfg = configByName[section.workflow_name];
        const cost = cfg?.config_cost ?? 0;
        const ops = cfg?.workflows ?? [];
        const drivers = cfg?.cost_drivers ?? [];
        const summary = substituteTemplate(section.summary_template, params);
        const disabled = section.disabled_when
          ? evalExpr(section.disabled_when, params)
          : false;

        return (
          <WorkflowSection
            key={section.workflow_name}
            configName={section.workflow_name}
            icon={section.icon}
            title={section.title}
            cost={cost}
            highlights={summary ? [summary, ...drivers] : drivers}
            workflows={ops}
            disabled={disabled}
            defaultExpanded
          />
        );
      })}
    </>
  );
}
